import React, { useState, useCallback, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Polyline, OverlayView } from '@react-google-maps/api';

const containerStyle = {
  width: '100%',
  height: '450px',
  borderRadius: '8px',
};

const LEG_COLORS = [
  '#FF1744', '#2979FF', '#FF9100', '#00BCD4', '#9C27B0',
  '#4CAF50', '#E91E63', '#FFC107', '#00E676', '#FF5722',
];

// Custom label badge overlay — tooltip style with pointer arrow
const LabelOverlay = ({ position, text, bgColor, dimmed }) => (
  <OverlayView
    position={position}
    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -h - 18 })}
  >
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: dimmed ? 0.25 : 1, transition: 'opacity 0.2s', pointerEvents: 'none' }}>
      <div style={{
        background: bgColor,
        color: '#fff',
        padding: '5px 12px',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: 800,
        letterSpacing: '0.5px',
        whiteSpace: 'nowrap',
        boxShadow: '0 3px 10px rgba(0,0,0,0.4)',
        border: '2px solid rgba(255,255,255,0.9)',
      }}>
        {text}
      </div>
      {/* Pointer triangle */}
      <div style={{
        width: 0, height: 0,
        borderLeft: '7px solid transparent',
        borderRight: '7px solid transparent',
        borderTop: `8px solid ${bgColor}`,
        marginTop: '-1px',
      }} />
    </div>
  </OverlayView>
);

const VehicleRouteMap = ({ visits, color, officeLocation }) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [map, setMap] = useState(null);
  const [tripPaths, setTripPaths] = useState([]); // { path, distanceKm, durationMin }
  const [selectedTrip, setSelectedTrip] = useState(null);
  const directionsFetched = useRef(false);

  const trips = buildTrips(visits, officeLocation);

  const onLoad = useCallback((mapInstance) => setMap(mapInstance), []);
  const onUnmount = useCallback(() => setMap(null), []);

  // Fetch directions per trip -> decode into path arrays for Polyline
  useEffect(() => {
    if (!isLoaded || !map || trips.length === 0 || directionsFetched.current) return;
    directionsFetched.current = true;

    const directionsService = new window.google.maps.DirectionsService();
    const results = new Array(trips.length).fill(null);
    let completed = 0;

    trips.forEach((trip, idx) => {
      const origin = { lat: officeLocation.lat, lng: officeLocation.lng };
      const waypoints = trip.pickupLocations.map(p => ({ location: p, stopover: true }));

      directionsService.route(
        {
          origin,
          destination: origin,
          waypoints: waypoints.slice(0, 25),
          optimizeWaypoints: false,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === 'OK') {
            const path = [];
            let totalDistM = 0;
            let totalDurS = 0;
            result.routes[0].legs.forEach(leg => {
              totalDistM += leg.distance.value;
              totalDurS += leg.duration.value;
              leg.steps.forEach(step => {
                step.path.forEach(p => path.push({ lat: p.lat(), lng: p.lng() }));
              });
            });
            results[idx] = {
              path,
              distanceKm: (totalDistM / 1000).toFixed(1),
              durationMin: Math.round(totalDurS / 60),
            };
          }
          completed++;
          if (completed === trips.length) {
            setTripPaths([...results]);
          }
        }
      );
    });
  }, [isLoaded, map, trips, officeLocation]);

  // Fit bounds
  useEffect(() => {
    if (!map || tripPaths.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(officeLocation);
    trips.forEach(trip => trip.pickupLocations.forEach(loc => bounds.extend(loc)));
    map.fitBounds(bounds, 50);
  }, [map, tripPaths, trips, officeLocation]);

  if (!isLoaded) return (
    <div style={{ height: '450px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2ff', borderRadius: '8px' }}>
      Loading map...
    </div>
  );

  const arrowSymbol = {
    path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
    scale: 4,
    fillColor: '#000',
    fillOpacity: 0.9,
    strokeColor: '#000',
    strokeWeight: 1,
  };

  return (
    <div style={{ marginBottom: '1rem' }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={officeLocation}
        zoom={12}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          zoomControl: true,
        }}
      >
        {/* Office Marker */}
        <Marker
          position={officeLocation}
          icon={{
            url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
            scaledSize: new window.google.maps.Size(40, 40),
          }}
          title="Office"
        />
        <LabelOverlay position={officeLocation} text="Office" bgColor="#2e7d32" dimmed={false} />

        {/* Per-trip routes + markers */}
        {trips.map((trip, tripIdx) => {
          const tripColor = LEG_COLORS[tripIdx % LEG_COLORS.length];
          const isSelected = selectedTrip === tripIdx;
          const isDimmed = selectedTrip !== null && !isSelected;

          return (
            <React.Fragment key={`trip-group-${tripIdx}`}>
              {/* Polyline with direction arrows */}
              {tripPaths[tripIdx] && (
                <Polyline
                  path={tripPaths[tripIdx].path}
                  options={{
                    strokeColor: tripColor,
                    strokeWeight: isSelected ? 7 : isDimmed ? 2 : 5,
                    strokeOpacity: isDimmed ? 0.2 : isSelected ? 1 : 0.75,
                    zIndex: isSelected ? 100 : isDimmed ? 1 : 10,
                    icons: [{
                      icon: isDimmed
                        ? { ...arrowSymbol, fillColor: '#ccc', fillOpacity: 0.4, strokeColor: '#ccc' }
                        : arrowSymbol,
                      offset: '20%',
                      repeat: '100px',
                    }],
                  }}
                  onClick={() => setSelectedTrip(isSelected ? null : tripIdx)}
                />
              )}

              {/* Pickup point markers */}
              {trip.pickupLocations.map((loc, i) => (
                <React.Fragment key={`trip-${tripIdx}-m-${i}`}>
                  <Marker
                    position={loc}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: isDimmed ? 6 : 9,
                      fillColor: tripColor,
                      fillOpacity: isDimmed ? 0.3 : 1,
                      strokeColor: '#fff',
                      strokeWeight: 2,
                    }}
                    opacity={isDimmed ? 0.35 : 1}
                    zIndex={isSelected ? 200 : 50}
                    onClick={() => setSelectedTrip(isSelected ? null : tripIdx)}
                  />
                  <LabelOverlay
                    position={loc}
                    text={trip.labels[i]}
                    bgColor={tripColor}
                    dimmed={isDimmed}
                  />
                </React.Fragment>
              ))}
            </React.Fragment>
          );
        })}
      </GoogleMap>

      {/* Legend - clickable */}
      {trips.length > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
          <div
            onClick={() => setSelectedTrip(null)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              background: selectedTrip === null ? '#e2e8f0' : '#f7fafc',
              padding: '0.5rem 0.85rem', borderRadius: '8px', fontSize: '0.85rem',
              cursor: 'pointer', border: selectedTrip === null ? '2px solid #667eea' : '2px solid transparent',
              transition: 'all 0.2s',
            }}
          >
            <strong>All Trips</strong>
          </div>
          {trips.map((trip, idx) => {
            const isActive = selectedTrip === idx;
            const meta = tripPaths[idx];
            return (
              <div
                key={idx}
                onClick={() => setSelectedTrip(isActive ? null : idx)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  background: isActive ? LEG_COLORS[idx % LEG_COLORS.length] + '18' : '#f7fafc',
                  padding: '0.5rem 0.85rem', borderRadius: '8px', fontSize: '0.85rem',
                  cursor: 'pointer',
                  border: isActive ? `2px solid ${LEG_COLORS[idx % LEG_COLORS.length]}` : '2px solid transparent',
                  transition: 'all 0.2s',
                  opacity: selectedTrip !== null && !isActive ? 0.5 : 1,
                }}
              >
                <span style={{ width: 12, height: 12, borderRadius: '50%', background: LEG_COLORS[idx % LEG_COLORS.length], display: 'inline-block', flexShrink: 0 }} />
                <span>
                  <strong>Trip {idx + 1}:</strong> {trip.labels.join(', ')}
                  {meta && (
                    <span style={{ color: '#666', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                      {meta.distanceKm} km · {meta.durationMin}m
                    </span>
                  )}
                </span>
              </div>
            );
          })}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#f7fafc', padding: '0.5rem 0.85rem', borderRadius: '8px', fontSize: '0.85rem' }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#2e7d32', display: 'inline-block' }} />
            <strong>Office</strong>
          </div>
        </div>
      )}
    </div>
  );
};

/** Groups visits into round-trips (contiguous pickup sequences) */
function buildTrips(visits) {
  if (!visits || visits.length === 0) return [];
  const trips = [];
  let i = 0;
  const all = visits.filter(v => v.lat != null && v.lng != null);
  while (i < all.length) {
    if (all[i].isPickup) {
      const pickups = [];
      const labels = [];
      while (i < all.length && all[i].isPickup) {
        pickups.push({ lat: all[i].lat, lng: all[i].lng });
        labels.push(all[i].shipmentLabel || `${all[i].shipmentIndex}`);
        i++;
      }
      trips.push({ pickupLocations: pickups, labels });
      while (i < all.length && !all[i].isPickup) i++;
    } else {
      i++;
    }
  }
  return trips;
}

export default VehicleRouteMap;
